import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDisclosure } from '@chakra-ui/react'
import { toast } from 'sonner'
import { getShareExtension, buildRawUrl, extractPasscodeFromUrl } from '@/lib/utils/share-url'
import { getShareStatusFromFields, getShareStatusBadge } from '@core/utils/share'
import { useCopyToClipboard } from '@/lib/hooks/useCopyToClipboard'
import { revokeShare, ApiError } from '@/lib/api/shares'
import {
  loadShareForManagement,
  decryptForEdit,
  reencryptAndUpdate,
} from '@/lib/shares/load'
import type { ShareDetails } from '@/types/share'

type ViewState = 'loading' | 'viewing' | 'error' | 'revoked'
type EditState = 'idle' | 'loading-content' | 'decrypting' | 'editing' | 'saving'

/**
 * Manages the share management lifecycle: loading share details with passcode
 * resolution, revoking, and the decrypt-edit-reencrypt flow.
 */
export function useShareManagement() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { isOpen: isRevokeOpen, onOpen: onRevokeOpen, onClose: onRevokeClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const [state, setState] = useState<ViewState>('loading')
  const [shareData, setShareData] = useState<(ShareDetails & { shareUrl: string }) | null>(null)
  const [error, setError] = useState('')
  const [isRevoking, setIsRevoking] = useState(false)
  const { copy, hasCopied } = useCopyToClipboard()

  // Edit modal state
  const [editState, setEditState] = useState<EditState>('idle')
  const [editPasscode, setEditPasscode] = useState('')
  const [editContent, setEditContent] = useState('')
  const [contentDecrypted, setContentDecrypted] = useState(false)

  // --- Load share ---

  useEffect(() => {
    loadShare()
  }, [token])

  const loadShare = async () => {
    if (!token) return

    try {
      const { share, isRevoked } = await loadShareForManagement(token)
      setShareData(share)
      setState(isRevoked ? 'revoked' : 'viewing')
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to load share details')
      }
      setState('error')
    }
  }

  // --- Revoke ---

  const handleRevoke = async () => {
    if (!token || !shareData) return

    setIsRevoking(true)
    try {
      await revokeShare(shareData.id, token)
      toast.success('Share has been revoked')
      setState('revoked')
      onRevokeClose()
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message)
      } else {
        toast.error('Failed to revoke share')
      }
    } finally {
      setIsRevoking(false)
    }
  }

  // --- Edit flow ---

  const handleOpenEdit = () => {
    setEditPasscode('')
    setEditContent('')
    setContentDecrypted(false)
    setEditState('idle')
    onEditOpen()
  }

  const handleCloseEdit = () => {
    setEditPasscode('')
    setEditContent('')
    setContentDecrypted(false)
    setEditState('idle')
    onEditClose()
  }

  const handleDecryptForEdit = async () => {
    if (!token || !shareData || !editPasscode) return
    if (!shareData.wrappedCek || !shareData.nonce || !shareData.kdfSalt || !shareData.kdfParams) return

    setEditState('loading-content')
    try {
      setEditState('decrypting')
      const content = await decryptForEdit(
        token,
        {
          wrappedCek: shareData.wrappedCek,
          nonce: shareData.nonce,
          kdfSalt: shareData.kdfSalt,
          kdfParams: shareData.kdfParams,
        },
        editPasscode,
      )

      setEditContent(content)
      setContentDecrypted(true)
      setEditState('editing')
    } catch (err) {
      console.error('Decryption failed:', err)
      toast.error('Incorrect passcode. Please try again.')
      setEditState('idle')
    }
  }

  const handleSaveEdit = async () => {
    if (!token || !shareData || !editPasscode || !contentDecrypted) return
    if (!shareData.wrappedCek || !shareData.kdfSalt || !shareData.kdfParams) return

    setEditState('saving')
    try {
      const { updatedAt, nonce } = await reencryptAndUpdate(
        token,
        editContent,
        {
          wrappedCek: shareData.wrappedCek,
          kdfSalt: shareData.kdfSalt,
          kdfParams: shareData.kdfParams,
        },
        editPasscode,
      )

      setShareData(prev => prev ? { ...prev, updatedAt, nonce } : null)

      toast.success('Share content updated successfully')
      handleCloseEdit()
    } catch (err) {
      console.error('Update failed:', err)
      if (err instanceof ApiError) {
        toast.error(err.message)
      } else {
        toast.error('Failed to update share. Please try again.')
      }
      setEditState('editing')
    }
  }

  // --- Clipboard helpers ---

  const copyToClipboard = () => {
    if (!shareData) return
    copy(shareData.shareUrl, 'share', 'Copied to clipboard')
  }

  const getRawUrl = () => {
    if (!shareData) return ''
    const passcode = extractPasscodeFromUrl(shareData.shareUrl)
    const extension = getShareExtension(null, shareData.format || 'md')
    return buildRawUrl(shareData.id, extension, passcode, shareData.isPublic)
  }

  const copyRawLinkToClipboard = () => {
    if (!shareData) return
    copy(getRawUrl(), 'raw', 'Raw link copied to clipboard')
  }

  const getStatus = () => {
    if (!shareData) return null
    return getShareStatusBadge(getShareStatusFromFields(shareData))
  }

  return {
    state,
    shareData,
    error,
    navigate,
    // Revoke
    isRevoking,
    isRevokeOpen,
    onRevokeOpen,
    onRevokeClose,
    handleRevoke,
    // Edit
    editState,
    editPasscode,
    setEditPasscode,
    editContent,
    setEditContent,
    contentDecrypted,
    isEditOpen,
    handleOpenEdit,
    handleCloseEdit,
    handleDecryptForEdit,
    handleSaveEdit,
    // Clipboard
    hasCopied,
    copyToClipboard,
    getRawUrl,
    copyRawLinkToClipboard,
    getStatus,
  }
}
